/**
 * <quick-add-variants> — inline variant pill quick-add for product cards.
 *
 * Replaces the modal-based "Choose options" quick-add with a single-tap
 * variant picker. Products with multiple variants render one pill per
 * variant; single-variant products render a round "+" bubble.
 *
 * Click flow:
 *   1. POST /cart/add.js with the chosen variant id + cart-drawer sections.
 *   2. Hand the response to cart-drawer (or cart-notification) via
 *      renderContents() so the drawer HTML + icon bubble update.
 *   3. Publish PUB_SUB_EVENTS.cartUpdate so CartDrawerItems reacts too.
 *   4. renderContents dispatches cart:updated; cart.js' normalizer handles
 *      the missing total_price by fetching /cart.js and re-dispatching, so
 *      the progress bar + gift selector reconcile automatically.
 */
if (!customElements.get('quick-add-variants')) {
  customElements.define(
    'quick-add-variants',
    class QuickAddVariants extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this._onClick = this._onClick.bind(this);
        this.addEventListener('click', this._onClick);
      }

      disconnectedCallback() {
        this._bound = false;
        this.removeEventListener('click', this._onClick);
      }

      _onClick(event) {
        const btn = event.target.closest('[data-quick-add-variant]');
        if (!btn || !this.contains(btn)) return;
        if (btn.disabled || btn.dataset.loading === 'true') return;
        if (btn.dataset.variantAvailable === 'false') return;
        event.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        this._addVariant(btn, variantId);
      }

      _resolveCartElement() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) return cartDrawer;
        return document.querySelector('cart-notification');
      }

      async _addVariant(btn, variantId) {
        const cart = this._resolveCartElement();
        btn.dataset.loading = 'true';
        btn.setAttribute('aria-busy', 'true');

        const body = {
          items: [{ id: Number(variantId), quantity: 1 }],
        };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart
            .getSectionsToRender()
            .map((s) => s.section || s.id)
            .join(',');
          body.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify(body),
          });

          const data = await res.json();
          if (!res.ok || data.status) {
            throw new Error((data && (data.description || data.message)) || 'Failed to add to cart');
          }

          this._showSuccess(btn);
          this._announce(this.querySelector('.quick-add-variants__status'), 'Added to cart');

          // Hand the response to the drawer so its HTML (line items, gift
          // selector, progress bar) updates and opens. If no drawer is on
          // the page, redirect to /cart so the customer still sees the add.
          if (cart && data.sections && typeof cart.renderContents === 'function') {
            if (data.items && data.items.length) {
              data.key = data.items[0].key;
              data.id = data.items[0].product_id;
            }
            cart.renderContents(data);
          } else if (!cart) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          // Notify the pub/sub bus for any other subscribers (price-per-item,
          // recipient-form, milestone discounts). cart-drawer's renderContents
          // already dispatches the DOM cart:updated event.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-variants',
              productVariantId: variantId,
              cartData: data,
            });
          }
        } catch (err) {
          console.error('[quick-add-variants]', err);
          this._showError(btn);
          this._announce(
            this.querySelector('.quick-add-variants__status'),
            (err && err.message) || 'Could not add to cart'
          );
        } finally {
          delete btn.dataset.loading;
          btn.removeAttribute('aria-busy');
        }
      }

      _showSuccess(btn) {
        btn.dataset.success = 'true';
        window.setTimeout(() => {
          delete btn.dataset.success;
        }, 900);
      }

      _showError(btn) {
        btn.dataset.error = 'true';
        window.setTimeout(() => {
          delete btn.dataset.error;
        }, 1200);
      }

      _announce(statusEl, msg) {
        if (!statusEl) return;
        statusEl.hidden = false;
        statusEl.textContent = msg;
        window.setTimeout(() => {
          statusEl.textContent = '';
          statusEl.hidden = true;
        }, 1500);
      }
    }
  );
}
